import React from 'react';
import { format } from 'date-fns';
import { convertSecondsToTime } from 'utils/timeHelper';
import {
  hasPatientProfileCcm,
  hasPatientProfileRpm,
} from 'utils/userTypeHelper';
import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
} from 'components/organisms/interactiveTime/hooks';
import { isCcmModule, isRpmModule } from 'infrastructure/functions';
import {
  ModuleType,
  type CcmStatuses,
  type RpmStatuses,
} from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import s from './styles.module.scss';

import type { IPatientDetails } from 'infrastructure/hooks/smsDashboard/usePatientInfo';
import type { RootState } from 'store';

interface IProps {
  patientInfo: IPatientDetails | undefined;
  patientGuid: string;
}

const InteractiveTime: React.FC<IProps> = ({ patientInfo, patientGuid }) => {
  const { t } = useTranslation();

  const { conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const { rpmInteractionTime, isFetching: isFetchRpm } =
    useGetRpmInteractionTime({
      patientGuid,
      enabled:
        hasPatientProfileRpm(patientInfo?.profile.status as RpmStatuses) &&
        isRpmModule(
          ModuleType[conversationType.toUpperCase() as keyof typeof ModuleType],
        ),
    });
  const { ccmInteractionTime, isFetching: isFetchCcm } =
    useGetCcmInteractionTime({
      patientGuid,
      enabled:
        hasPatientProfileCcm(patientInfo?.profile.ccmStatus as CcmStatuses) &&
        isCcmModule(
          ModuleType[conversationType.toUpperCase() as keyof typeof ModuleType],
        ),
    });

  const interactiveTime = ccmInteractionTime || rpmInteractionTime;

  return (
    <div className={s['interactive-data']}>
      <div className={s['interactive-block']}>
        <div className={s['interactive-label']}>
          <p>{t('smsDashboard.total')}</p>
        </div>
        <div className={s['interactive-text']}>
          <p>
            {isFetchCcm || isFetchRpm
              ? '--:--:--'
              : convertSecondsToTime(interactiveTime?.totalTime)}
          </p>
        </div>
      </div>
      <div className={s['interactive-block']}>
        <div className={s['interactive-label']}>
          <p>{t('smsDashboard.clinic')}</p>
        </div>
        <div className={s['interactive-text']}>
          <p>
            {isFetchCcm || isFetchRpm
              ? '--:--:--'
              : convertSecondsToTime(interactiveTime?.clinicTime)}
          </p>
        </div>
      </div>
      <div className={s['interactive-date']}>
        <p className={s['chat-profile--center']}>
          {format(new Date(), 'MMMM')}
        </p>
      </div>
    </div>
  );
};

export default InteractiveTime;
